import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { NewAddedPage } from 'types/Page';
import { SocialLoginPage, SocialLoginTokenType } from 'types/SocialLogin';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { modalRoutes } from 'constants/routes';
import { useModalHistory } from 'context/ModalRouter';
import { ApiClientError } from 'api/client';
import PageTypeUtils from 'app/utils/pageType';
import HeapUtils from 'app/utils/heap';
import {
  getConnectErrorCode,
  getConnectNetwork,
} from 'app/modules/socialLogin/utils/connectTracking';
import { SocialProfile } from 'components/modals/newSocialMediaProfile/selectSocialProfiles/SelectSocialProfileOption';

export interface UseSocialProfilesSelectionParams {
  data: SocialLoginPage[];
  pageType: number;
  connectedPagesExternalIds: string[];
  onPagesCreate: (page: NewAddedPage) => void;
  onError?: (error: ApiClientError) => void;
  isSingleSelect?: boolean;
  clientBusinessId?: string | null;
  tokenType?: SocialLoginTokenType;
  userKey?: string | null;
}

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const toSocialProfiles = (data: SocialLoginPage[]): SocialProfile[] =>
  data.map((profile) => ({
    id: profile.external_id,
    name: profile.name,
    logo: profile.logo
      ? {
          src: profile.logo,
        }
      : undefined,
    type: PageTypeUtils.getNumberFromName(profile.type),
    businessId: profile.business_id,
    businessName: profile.business_name,
  }));

const uniqueByExternalId = (data: SocialLoginPage[]) =>
  Array.from(new Map(data.map((page) => [page.external_id, page])).values());

const useSocialProfilesSelection = ({
  data: rawData,
  pageType,
  connectedPagesExternalIds,
  onPagesCreate,
  onError,
  isSingleSelect,
  clientBusinessId,
  tokenType,
  userKey,
}: UseSocialProfilesSelectionParams) => {
  const data = useMemo(() => uniqueByExternalId(rawData), [rawData]);
  const isAlreadyConnectedProfile = (id: string) =>
    connectedPagesExternalIds.includes(id);

  const [selectedProfiles, setSelectedProfiles] = useState<string[]>(() =>
    data
      .filter((page) => isAlreadyConnectedProfile(page.external_id))
      .map((page) => page.external_id),
  );
  const { hasReachedProfilesLimit } = useSubscriptionInfo();
  const modalHistory = useModalHistory();
  const location = useLocation();
  const network = getConnectNetwork(pageType);
  const hasTrackedListed = useRef(false);

  useEffect(() => {
    if (hasTrackedListed.current || data.length === 0) {
      return;
    }

    hasTrackedListed.current = true;
    HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_LISTED, {
      network,
      profiles_available_count: data.length,
      profiles_already_connected_count: data.filter((page) =>
        connectedPagesExternalIds.includes(page.external_id),
      ).length,
    });
  }, [data, network, connectedPagesExternalIds]);

  const { mutate: mutateAddProfiles, isLoading } = useMutation<
    number[],
    ApiClientError,
    {
      pageType: number;
      data: {
        timezone: string;
        pages: SocialLoginPage[];
      };
    }
  >(
    (variables) =>
      SocialLoginApi.addProfiles(variables.pageType, {
        timezone,
        pages: variables.data.pages,
        clientBusinessId: clientBusinessId ?? null,
        ...(tokenType === 'system_user_obo'
          ? { tokenType, userKey: userKey ?? null }
          : {}),
      }),
    {
      onSuccess: onSuccess,
      onError: (error) => {
        HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SAVE_FAILED, {
          network,
          error_code: getConnectErrorCode(error),
        });
        onError?.(error);
      },
    },
  );

  function onSuccess(
    pageIds: number[],
    variables: { data: { pages: SocialLoginPage[] } },
  ) {
    if (pageIds && pageIds.length > 0) {
      const newCount = variables.data.pages.filter(
        (page) => !isAlreadyConnectedProfile(page.external_id),
      ).length;

      HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SAVED, {
        network,
        count: newCount,
        profile_type: 'real',
      });
      onPagesCreate({
        ids: pageIds,
        type: TYPE.FACEBOOK,
        newCount,
      });
    }
  }

  const openProfileLimitModal = () =>
    modalHistory.push({
      hash: modalRoutes.PROFILE_LIMIT_REACHED,
      search: location.search,
    });

  const handleSelect = (id: string) => {
    if (
      !selectedProfiles.includes(id) &&
      !isAlreadyConnectedProfile(id) &&
      hasReachedProfilesLimit(
        selectedProfiles.filter((id) => !isAlreadyConnectedProfile(id)).length +
          1,
      )
    ) {
      openProfileLimitModal();
      return;
    }

    setSelectedProfiles((prev) => {
      if (isSingleSelect) {
        return [id];
      }

      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleSubmit = () => {
    const pages = selectedProfiles.map((id) =>
      data.find((profile) => profile.external_id === id),
    ) as SocialLoginPage[];

    HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SUBMITTED, {
      network,
      selected_count: pages.length,
      new_selected_count: pages.filter(
        (page) => !isAlreadyConnectedProfile(page.external_id),
      ).length,
    });
    mutateAddProfiles({
      pageType,
      data: {
        timezone,
        pages,
      },
    });
  };

  const handleSelectAll = (filteredIds: (number | string)[]) => {
    const filteredExternalIds = filteredIds.map((id) => String(id));

    if (
      hasReachedProfilesLimit(
        filteredExternalIds.filter((id) => !isAlreadyConnectedProfile(id))
          .length,
      )
    ) {
      openProfileLimitModal();
      return;
    }
    setSelectedProfiles((prev) =>
      Array.from(new Set([...prev, ...filteredExternalIds])),
    );
  };

  const handleSelectNone = () => setSelectedProfiles([]);

  return {
    profiles: toSocialProfiles(data),
    selectedProfiles,
    isSubmitting: isLoading,
    handleSelect,
    handleSelectAll,
    handleSelectNone,
    handleSubmit,
  };
};

export default useSocialProfilesSelection;
